// Firebase imports
import { 
  collection, 
  addDoc, 
  getDoc,
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError, toDRXError } from '@/lib/drx-error';
import { ITagRepository } from './interfaces';

// Tag entity interfaces
export interface CreateTagData {
  name: string;
  slug: string;
  description?: string;
  color?: string;
  category: 'course' | 'resource' | 'lesson' | 'module' | 'general';
  isActive: boolean;
  usageCount?: number;
}

export interface UpdateTagData {
  name?: string;
  slug?: string;
  description?: string;
  color?: string;
  category?: 'course' | 'resource' | 'lesson' | 'module' | 'general';
  isActive?: boolean;
  usageCount?: number;
  updatedAt?: Timestamp;
}

export interface TagFilter {
  category?: 'course' | 'resource' | 'lesson' | 'module' | 'general';
  isActive?: boolean;
  nameContains?: string;
}

export interface GetTagData {
  id: string;
  name: string;
  slug: string;
  description?: string;
  color?: string;
  category: 'course' | 'resource' | 'lesson' | 'module' | 'general';
  isActive: boolean;
  usageCount: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export class TagRepository implements ITagRepository {
  constructor(private db: any) {
    console.log('[TagRepository] Constructor - Injected Firebase Admin DB instance:', !!this.db);
  }

  private collectionName = 'tags';

  private toRepositoryError(
    error: unknown,
    code: string,
    devMessage: string,
    context?: Record<string, unknown>
  ) {
    return toDRXError(error, {
      code,
      devMessage,
      context,
    });
  }

  // Create tag
  async createTag(tagData: CreateTagData): Promise<GetTagData> {
    try {
      // Check if slug already exists
      const existingTag = await this.getTagBySlug(tagData.slug);
      if (existingTag) {
        throw new DRXError({
          code: 'TAG_SLUG_CONFLICT',
          devMessage: `Tag slug ${tagData.slug} already exists`,
          userMessage: 'A tag with this slug already exists.',
          level: 'warning',
          context: { slug: tagData.slug, existingTagId: existingTag.id, name: tagData.name },
        });
      }

      const docRef = await addDoc(collection(db, this.collectionName), {
        ...tagData,
        isActive: tagData.isActive ?? true,
        usageCount: tagData.usageCount ?? 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'TAG_CREATE_MISSING_DOCUMENT',
          devMessage: 'Tag document was missing after create',
          context: { slug: tagData.slug, name: tagData.name, category: tagData.category },
        });
      }

      return {
        id: docRef.id,
        ...docSnap.data(),
      } as GetTagData;
    } catch (error) {
      console.error('Error creating tag:', error);
      throw this.toRepositoryError(error, 'TAG_CREATE_FAILED', 'Failed to create tag', {
        slug: tagData.slug,
        name: tagData.name,
        category: tagData.category,
      });
    }
  }

  // Get tag by ID
  async getTagById(tagId: string): Promise<GetTagData | null> {
    try {
      const docRef = doc(db, this.collectionName, tagId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as GetTagData;
    } catch (error) {
      console.error('Error getting tag by ID:', error);
      throw this.toRepositoryError(error, 'TAG_FETCH_FAILED', `Failed to get tag ${tagId}`, { tagId });
    }
  }

  // Get tag by slug
  async getTagBySlug(slug: string): Promise<GetTagData | null> {
    try {
      const q = query(collection(db, this.collectionName), where('slug', '==', slug));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
      } as GetTagData;
    } catch (error) {
      console.error('Error getting tag by slug:', error);
      throw this.toRepositoryError(error, 'TAG_BY_SLUG_FETCH_FAILED', `Failed to get tag by slug ${slug}`, { slug });
    }
  }

  // Get tags with filters
  async getTags(filter: TagFilter = {}, limitCount?: number): Promise<GetTagData[]> {
    try {
      let q = collection(db, this.collectionName);

      // Build query with filters
      if (filter.category) {
        q = query(q, where('category', '==', filter.category));
      }
      if (filter.isActive !== undefined) {
        q = query(q, where('isActive', '==', filter.isActive));
      }

      // Add ordering and limit
      q = query(q, orderBy('name', 'asc'));
      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const querySnapshot = await getDocs(q);
      let tags: GetTagData[] = [];

      querySnapshot.forEach((doc) => {
        tags.push({
          id: doc.id,
          ...doc.data(),
        } as GetTagData);
      });

      // Apply name filter (since Firestore doesn't support contains queries easily)
      if (filter.nameContains) {
        const searchTerm = filter.nameContains.toLowerCase();
        tags = tags.filter(tag =>
          tag.name.toLowerCase().includes(searchTerm) ||
          tag.slug.toLowerCase().includes(searchTerm)
        );
      }

      return tags;
    } catch (error) {
      console.error('Error getting tags:', error);
      throw this.toRepositoryError(error, 'TAGS_FETCH_FAILED', 'Failed to get tags', { filter, limitCount });
    }
  }

  // Update tag
  async updateTag(tagId: string, updateData: UpdateTagData): Promise<GetTagData> {
    try {
      // Check slug uniqueness if updating slug
      if (updateData.slug) {
        const existingTag = await this.getTagBySlug(updateData.slug);
        if (existingTag && existingTag.id !== tagId) {
          throw new DRXError({
            code: 'TAG_SLUG_CONFLICT',
            devMessage: `Tag slug ${updateData.slug} already exists`,
            userMessage: 'A tag with this slug already exists.',
            level: 'warning',
            context: { tagId, slug: updateData.slug, existingTagId: existingTag.id },
          });
        }
      }

      const docRef = doc(db, this.collectionName, tagId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'TAG_NOT_FOUND_AFTER_UPDATE',
          devMessage: `Tag ${tagId} not found after update`,
          userMessage: 'Tag not found.',
          level: 'warning',
          context: { tagId, updateData },
        });
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as GetTagData;
    } catch (error) {
      console.error('Error updating tag:', error);
      throw this.toRepositoryError(error, 'TAG_UPDATE_FAILED', `Failed to update tag ${tagId}`, {
        tagId,
        updateData,
      });
    }
  }

  // Delete tag
  async deleteTag(tagId: string): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, tagId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting tag:', error);
      throw this.toRepositoryError(error, 'TAG_DELETE_FAILED', `Failed to delete tag ${tagId}`, { tagId });
    }
  }

  // Get tag by name
  async getTagByName(name: string): Promise<GetTagData | null> {
    try {
      const q = query(collection(db, this.collectionName), where('name', '==', name));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
      } as GetTagData;
    } catch (error) {
      console.error('Error getting tag by name:', error);
      throw this.toRepositoryError(error, 'TAG_BY_NAME_FETCH_FAILED', `Failed to get tag by name ${name}`, { name });
    }
  }

  // Get active tags
  async getActiveTags(limitCount?: number): Promise<GetTagData[]> {
    try {
      const filter: TagFilter = { isActive: true };
      return this.getTags(filter, limitCount);
    } catch (error) {
      console.error('Error getting active tags:', error);
      throw this.toRepositoryError(error, 'TAGS_ACTIVE_FETCH_FAILED', 'Failed to get active tags', { limitCount });
    }
  }

  // Get inactive tags
  async getInactiveTags(limitCount?: number): Promise<GetTagData[]> {
    try {
      const filter: TagFilter = { isActive: false };
      return this.getTags(filter, limitCount);
    } catch (error) {
      console.error('Error getting inactive tags:', error);
      throw this.toRepositoryError(error, 'TAGS_INACTIVE_FETCH_FAILED', 'Failed to get inactive tags', { limitCount });
    }
  }

  // Get tags by owner (creator)
  async getTagsByOwner(ownerId: string, limitCount?: number): Promise<GetTagData[]> {
    try {
      // Tags don't have an owner field in the current schema
      // This would require adding a createdBy field to the tag schema
      console.log('Tags by owner not supported - no owner field in schema');
      return [];
    } catch (error) {
      console.error('Error getting tags by owner:', error);
      throw this.toRepositoryError(error, 'TAGS_BY_OWNER_FETCH_FAILED', `Failed to get tags by owner ${ownerId}`, { ownerId, limitCount });
    }
  }

  // Get all tags (admin only) or active tags
  async getTagsForUser(userId: string, isAdmin: boolean = false, limitCount?: number): Promise<GetTagData[]> {
    try {
      if (isAdmin) {
        // Admin sees all tags
        return this.getTags({}, limitCount);
      } else {
        // Regular user sees only active tags
        return this.getActiveTags(limitCount);
      }
    } catch (error) {
      console.error('Error getting tags for user:', error);
      throw this.toRepositoryError(error, 'TAGS_FOR_USER_FETCH_FAILED', `Failed to get tags for user ${userId}`, { userId, isAdmin, limitCount });
    }
  }

  // Get all tags (admin only)
  async getAllTags(limitCount?: number): Promise<GetTagData[]> {
    try {
      return this.getTags({}, limitCount);
    } catch (error) {
      console.error('Error getting all tags:', error);
      throw this.toRepositoryError(error, 'TAGS_ALL_FETCH_FAILED', 'Failed to get all tags', { limitCount });
    }
  }

  // Get tags by category
  async getTagsByCategory(category: 'course' | 'resource' | 'lesson' | 'module' | 'general', limitCount?: number): Promise<GetTagData[]> {
    try {
      const filter: TagFilter = { category, isActive: true };
      return this.getTags(filter, limitCount);
    } catch (error) {
      console.error('Error getting tags by category:', error);
      throw this.toRepositoryError(error, 'TAGS_BY_CATEGORY_FETCH_FAILED', `Failed to get tags by category ${category}`, { category, limitCount });
    }
  }

  // Search tags
  async searchTags(searchTerm: string, category?: 'course' | 'resource' | 'lesson' | 'module' | 'general', limitCount?: number): Promise<GetTagData[]> {
    try {
      const filter: TagFilter = {
        nameContains: searchTerm,
        isActive: true,
      };

      if (category) {
        filter.category = category;
      }

      return this.getTags(filter, limitCount);
    } catch (error) {
      console.error('Error searching tags:', error);
      throw this.toRepositoryError(error, 'TAGS_SEARCH_FAILED', `Failed to search tags for ${searchTerm}`, { searchTerm, category, limitCount });
    }
  }

  // Increment tag usage count
  async incrementUsageCount(tagId: string): Promise<void> {
    try {
      const tag = await this.getTagById(tagId);
      if (!tag) {
        throw new DRXError({
          code: 'TAG_NOT_FOUND',
          devMessage: `Tag ${tagId} not found`,
          userMessage: 'Tag not found.',
          level: 'warning',
          context: { tagId },
        });
      }

      await this.updateTag(tagId, {
        usageCount: tag.usageCount + 1,
      });
    } catch (error) {
      console.error('Error incrementing usage count:', error);
      throw this.toRepositoryError(error, 'TAG_USAGE_INCREMENT_FAILED', `Failed to increment usage count for tag ${tagId}`, { tagId });
    }
  }

  // Decrement tag usage count
  async decrementUsageCount(tagId: string): Promise<void> {
    try {
      const tag = await this.getTagById(tagId);
      if (!tag) {
        throw new DRXError({
          code: 'TAG_NOT_FOUND',
          devMessage: `Tag ${tagId} not found`,
          userMessage: 'Tag not found.',
          level: 'warning',
          context: { tagId },
        });
      }

      await this.updateTag(tagId, {
        usageCount: Math.max(0, tag.usageCount - 1),
      });
    } catch (error) {
      console.error('Error decrementing usage count:', error);
      throw this.toRepositoryError(error, 'TAG_USAGE_DECREMENT_FAILED', `Failed to decrement usage count for tag ${tagId}`, { tagId });
    }
  }

  // Get popular tags
  async getPopularTags(limitCount: number = 10): Promise<GetTagData[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('isActive', '==', true),
        orderBy('usageCount', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const tags: GetTagData[] = [];

      querySnapshot.forEach((doc) => {
        tags.push({
          id: doc.id,
          ...doc.data(),
        } as GetTagData);
      });

      return tags;
    } catch (error) {
      console.error('Error getting popular tags:', error);
      throw this.toRepositoryError(error, 'TAGS_POPULAR_FETCH_FAILED', 'Failed to get popular tags', { limitCount });
    }
  }

  // Bulk create tags
  async bulkCreateTags(tagsData: CreateTagData[]): Promise<GetTagData[]> {
    try {
      const createdTags: GetTagData[] = [];

      for (const tagData of tagsData) {
        try {
          const tag = await this.createTag(tagData);
          createdTags.push(tag);
        } catch (error) {
          console.error(`Failed to create tag "${tagData.name}":`, error);
          // Continue with other tags
        }
      }

      return createdTags;
    } catch (error) {
      console.error('Error bulk creating tags:', error);
      throw this.toRepositoryError(error, 'TAGS_BULK_CREATE_FAILED', 'Failed to bulk create tags', { count: tagsData.length });
    }
  }

  // Bulk update tags
  async bulkUpdateTags(updates: { tagId: string; data: UpdateTagData }[]): Promise<GetTagData[]> {
    try {
      const updatedTags: GetTagData[] = [];

      for (const { tagId, data } of updates) {
        try {
          const tag = await this.updateTag(tagId, data);
          updatedTags.push(tag);
        } catch (error) {
          console.error(`Failed to update tag "${tagId}":`, error);
          // Continue with other tags
        }
      }

      return updatedTags;
    } catch (error) {
      console.error('Error bulk updating tags:', error);
      throw this.toRepositoryError(error, 'TAGS_BULK_UPDATE_FAILED', 'Failed to bulk update tags', { count: updates.length });
    }
  }

  // Generate slug from name
  generateSlug(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .trim();
  }

  // Get tags by type (absorbed from firestore.ts)
  async getTagsByType(type: string): Promise<GetTagData[]> {
    try {
      const q = query(collection(db, this.collectionName), where('type', '==', type));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as GetTagData));
    } catch (error) {
      throw new DRXError({
        code: 'TAG_TYPE_FETCH_FAILED',
        devMessage: `Failed to fetch tags of type ${type}`,
        cause: error,
      });
    }
  }
}
